import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { QLookup, SearchResource, useToastProvider } from '@qualio/ui-components';
import qriServiceApi from '../api/qriServiceApi.api';
import { DefaultErrorMessage } from '../displayStrings';

export const ResolveRelatedRecordsQueryKey = 'resolveRelatedRecords';

export type ResolvedRelatedRecord = {
  qri: string;
  code: string;
  name: string;
  /** Drives the per-resource icon on the chip (e.g. `document`, `quality-event`). */
  resource?: SearchResource;
};

/**
 * Turns the active `related_records` QRIs into `{ qri, code, name }` for chip display.
 *
 * In-session selections already carry `code`/`name` on the picker's `V2Result`, so those
 * are reused directly. On a fresh load (only QRIs in the URL) the remaining QRIs are
 * batch-resolved via `qriApi` (through `qriServiceApi`, which takes QRIs directly). A
 * loading state is exposed so chips can show a spinner until names resolve.
 */
export const useResolveRelatedRecords = (
  qris: string[],
  selectedResults: QLookup.DataProvider.V2Result[] = [],
): { relatedRecords: ResolvedRelatedRecord[]; isLoading: boolean } => {
  const { showToast } = useToastProvider();

  const selectedByQri = useMemo(() => {
    const map = new Map<string, QLookup.DataProvider.V2Result>();
    selectedResults.forEach((result) => map.set(result.identifiers.qri, result));
    return map;
  }, [selectedResults]);

  // Only QRIs that arrived via the URL (with no matching in-session selection) need a fetch.
  const qrisToFetch = useMemo(() => qris.filter((qri) => !selectedByQri.has(qri)).sort(), [qris, selectedByQri]);

  const { data, isLoading } = useQuery(
    [ResolveRelatedRecordsQueryKey, qrisToFetch],
    () => qriServiceApi.getBulkDetails(qrisToFetch),
    {
      enabled: qrisToFetch.length > 0,
      onError: () => {
        showToast({ description: DefaultErrorMessage, status: 'error' });
      },
    },
  );

  const relatedRecords = useMemo<ResolvedRelatedRecord[]>(() => {
    return qris.map((qri) => {
      const selected = selectedByQri.get(qri);
      if (selected) {
        return {
          qri,
          code: selected.identifiers.code ?? '',
          name: selected.name,
          resource: selected.resource as SearchResource,
        };
      }

      const resolved = data?.[qri];
      const code =
        resolved && 'identifiers' in resolved && 'code' in resolved.identifiers ? resolved.identifiers.code : '';
      const name = resolved && 'name' in resolved ? resolved.name : '';
      const resource = resolved && 'resource' in resolved ? (resolved.resource as SearchResource) : undefined;
      return { qri, code: code ?? '', name, resource };
    });
  }, [qris, selectedByQri, data]);

  return { relatedRecords, isLoading: qrisToFetch.length > 0 && isLoading };
};
